/* ============================================================
   src/shared/zohoImport.ts
   ----------------------------------------------------------------
   Shared contract for the Zoho appointment importer (the "Import
   from Zoho" picker). Both desktop (App.tsx) and mobile
   (CustomerCard) talk to the same `zoho-appointments` edge function
   via supabase.functions.invoke; this module holds the one copy of
   the allow-list, the wire types, and the gate helper so the two
   shells can't drift.

   The edge function enforces its own ZOHO_ALLOWED_EMAILS server-side,
   so the client gate below is UI hygiene only (hide a button that
   would 403 anyway).
   ============================================================ */

import { supabase } from "../supabaseClient";

/** Emails allowed to see/use the Zoho import button. The edge function
 *  enforces the same allow-list server-side. */
export const ZOHO_IMPORT_ALLOWED_EMAILS = new Set<string>([
  "jaycolapinto@gmail.com",
]);

/** True when `email` may use the Zoho import button. Case-insensitive. */
export function canImportFromZoho(email: string | null | undefined): boolean {
  return ZOHO_IMPORT_ALLOWED_EMAILS.has((email || "").toLowerCase());
}

/** One row from the `list` action — an appointment with a linked contact. */
export type ZohoAppt = {
  eventId: string;
  title: string;
  startTime: string;
  contactId: string;
  contactName: string;
  contactModule: string;
};

/* ----------------------------------------------------------------
   Day-stepper date helpers (shared by both pickers). The picker shows
   one NY calendar day at a time; the edge function's `list` action
   accepts an optional `date` ("YYYY-MM-DD") and filters Events to that
   day in America/New_York (omit = today, backward compatible).
   ---------------------------------------------------------------- */

/** Today's date in America/New_York as "YYYY-MM-DD" — the picker's default. */
export function nyTodayDate(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

/** Shift a "YYYY-MM-DD" calendar date by `delta` whole days. Computed in UTC
 *  (Date.UTC handles month/year rollover) so it's immune to the viewer's local
 *  timezone and DST. */
export function shiftISODate(dateStr: string, delta: number): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(Date.UTC(y, (m || 1) - 1, (d || 1) + delta));
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

/** Day-of-week for a "YYYY-MM-DD" calendar date: 0 = Sunday … 6 = Saturday.
 *  Computed in UTC (a pure calendar date has a fixed weekday) so it's immune to
 *  the viewer's timezone. */
export function isoDayOfWeek(dateStr: string): number {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(Date.UTC(y, (m || 1) - 1, d || 1)).getUTCDay();
}

/** Step the picker's selected day by `delta` (±1), skipping Sunday in whichever
 *  direction we're moving — there are never Sunday meetings. Forward (delta > 0)
 *  onto a Sunday → Monday; backward (delta < 0) onto a Sunday → Saturday. */
export function stepApptDate(dateStr: string, delta: number): string {
  const next = shiftISODate(dateStr, delta);
  if (isoDayOfWeek(next) === 0) {
    return shiftISODate(next, delta >= 0 ? 1 : -1);
  }
  return next;
}

/** "2026-06-13" → "Friday, June 13" (America/New_York). Anchored at noon UTC,
 *  which is still the same calendar day in NY (UTC-4/-5), so the weekday is
 *  correct regardless of the viewer's timezone. */
export function formatApptDateLabel(dateStr: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return "";
  return new Date(`${dateStr}T12:00:00Z`).toLocaleDateString("en-US", {
    timeZone: "America/New_York",
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

/* ----------------------------------------------------------------
   Last-name search (desk companion to the day picker). The edge
   function's `search` action queries Contacts + Leads by
   Last_Name:starts_with and returns matches newest-first; picking one
   goes through the SAME `resolve` action the day picker uses, so the
   two paths can't drift on field mapping.

   Lives here (not in App.tsx) so the mobile shell can adopt it later
   as a pure-UI addition.
   ---------------------------------------------------------------- */

/** Shorter than this and we don't call Zoho at all — one letter matches
 *  thousands of records. Mirrored server-side. */
export const ZOHO_SEARCH_MIN_CHARS = 2;

/** Type-ahead debounce. Long enough that a normal typist spends one call
 *  per name, short enough to feel live. */
export const ZOHO_SEARCH_DEBOUNCE_MS = 350;

/** One row from the `search` action — a person, not an appointment. */
export type ZohoSearchResult = {
  id: string;
  module: string;
  firstName: string;
  lastName: string;
  fullName: string;
  city: string;
  state: string;
  phone: string;
  createdTime: string;
};

/** "Smith, John" — last name first, since that's what was searched. Falls
 *  back to whatever half exists (Zoho records are frequently partial). */
export function formatSearchResultName(r: ZohoSearchResult): string {
  const last = (r.lastName || "").trim();
  const first = (r.firstName || "").trim();
  if (last && first) return `${last}, ${first}`;
  return last || first || r.fullName || "(no name)";
}

/** "Commack, NY" — the disambiguator when a common last name returns dozens. */
export function formatSearchResultPlace(r: ZohoSearchResult): string {
  const city = (r.city || "").trim();
  const state = (r.state || "").trim().toUpperCase();
  if (city && state) return `${city}, ${state}`;
  return city || state || "";
}

/** Year of a record's Created_Time, but only when it isn't the current year —
 *  an extra age cue on old duplicates without cluttering this season's rows. */
export function searchResultYear(createdTime: string): string {
  const year = (createdTime || "").slice(0, 4);
  if (!/^\d{4}$/.test(year)) return "";
  return year === String(new Date().getFullYear()) ? "" : year;
}

/** The contact shape the `resolve` action returns. All fields optional —
 *  Zoho records are frequently partial. */
export type ZohoContact = {
  firstName?: string;
  lastName?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  email?: string;
};

/** Outcome of one `search` call. Never throws: `failed` distinguishes "Zoho
 *  is unreachable" from the equally-empty "nobody by that name", so the
 *  caller can show the right message without a try/catch of its own. */
export type ZohoSearchOutcome = {
  items: ZohoSearchResult[];
  capped: boolean;
  failed: boolean;
};

/** Run the `search` action. Deliberately soft-failing: a search that can't
 *  reach Zoho — including an older deployment of the edge function that
 *  doesn't know the `search` action yet and 400s — resolves to an empty
 *  list with `failed: true` rather than rejecting. The day picker must keep
 *  working no matter what search does. */
export async function searchZohoByLastName(
  lastName: string
): Promise<ZohoSearchOutcome> {
  const q = (lastName || "").trim();
  if (q.length < ZOHO_SEARCH_MIN_CHARS) {
    return { items: [], capped: false, failed: false };
  }

  try {
    const { data, error } = await supabase.functions.invoke(
      "zoho-appointments",
      { body: { action: "search", lastName: q } }
    );
    if (error) throw error;

    const items: ZohoSearchResult[] = Array.isArray((data as any)?.items)
      ? (data as any).items
      : [];
    return { items, capped: !!(data as any)?.capped, failed: false };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("Zoho search failed", e);
    return { items: [], capped: false, failed: true };
  }
}
